import React, { useContext } from 'react';
import { User } from '@landr/root-app/auth';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { SamplesLanguages } from 'types/enums';
import { sendLogout } from 'helpers';
import useAuthenticationEffects from './useAuthenticationEffects.hook';

export type AuthenticationContextType = {
    isAuthenticated: boolean;
    isGuestUser: boolean;
    isLoading: boolean;
    user: User | null;
    locale: SamplesLanguages | null;

    /**
     * In theory, you do not need to use those,
     * Instead, you should be using the auth service directly
     *
     * But we found an issue with an external dependency (our UNB) loosing the `this` object
     * when auth.[method] is props drilled.
     */
    login: () => void;
    signup: () => void;
    logout: () => void;
};

export const defaultAuthenticationContext: AuthenticationContextType = {
    isAuthenticated: false,
    isGuestUser: false,
    isLoading: true,
    user: null,
    locale: null,
    login: () => undefined,
    signup: () => undefined,
    logout: () => undefined,
};

export const AuthenticationContext = React.createContext(
    defaultAuthenticationContext,
);

export const useAuthentication = (): AuthenticationContextType =>
    useContext(AuthenticationContext);

interface AuthenticationProviderProps {
    children: React.ReactNode;
}

export const AuthenticationProvider = ({
    children,
}: AuthenticationProviderProps): JSX.Element => {
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const {
        isAuthenticated,
        isGuestUser,
        isLoading,
        user,
        locale,
        login,
        signup,
        logout,
    } = useAuthenticationEffects({ isDAWPlugin, sendLogout });

    return (
        <AuthenticationContext.Provider
            value={{
                isAuthenticated,
                isGuestUser,
                isLoading,
                user,
                locale,
                login,
                signup,
                logout,
            }}
        >
            <>{children}</>
        </AuthenticationContext.Provider>
    );
};
